/**
 * BOOKR Class SSO Integration Service
 * Handles token issuance and launching BOOKR with pre-authentication
 */

/**
 * Issues a BOOKR SSO token and launches BOOKR Class
 * @param {string} userId - The user ID to create token for
 * @param {Object} options - Optional configuration
 * @param {number} options.ttlSeconds - Token time-to-live (60-1800 seconds)
 * @param {string} options.returnUrl - URL to return to after BOOKR session
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function launchBookrSso(userId, options = {}) {
  try {
    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('REACT_APP_ADMIN_API_KEY not configured');
    }

    const requestBody = {
      userId: userId,
      ttlSeconds: options.ttlSeconds || 300,
      returnUrl: options.returnUrl || window.location.origin,
    };

    let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      if (process.env.NODE_ENV === 'production') {
        const error = new Error('Configuration Error: REACT_APP_API_BASE_URL is not set in production');
        error.code = 'CONFIG_ERROR';
        throw error;
      }
      apiBaseUrl = 'http://localhost:5001';
    }
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    const fullUrl = `${apiBaseUrl}/providers/bookr-class/issue-token`;

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': adminApiKey,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Failed to issue token');
    }

    const redirectUrl = data.redirectUrl || data.launchUrl;
    window.open(redirectUrl, "_blank", "noopener,noreferrer");

    return {
      success: true,
      token: data.token,
      launchUrl: data.launchUrl,
      expiresAt: data.expiresAt,
    };
  } catch (error) {
    const errorMessage = error.message || 'Failed to launch BOOKR. Please try again.';
    return {
      success: false,
      error: errorMessage,
      details: error,
    };
  }
}


/**
 * Check if a provider is BOOKR Class
 * @param {Object} provider - Provider object from database
 * @returns {boolean} True if this is BOOKR Class provider
 */
export function isBookrProvider(provider) {
  if (!provider) return false;

  // Check by provider key (most reliable)
  if (provider.key === 'bookr-class' || provider.key === 'bookr_class') {
    return true;
  }

  // Check by name (fallback)
  const name = (provider.name || '').toLowerCase();
  return name.includes('bookr') && (name.includes('class') || name.includes('sso'));
}


/**
 * Handle provider click - either launch SSO or open website
 * @param {Object} provider - Provider object
 * @param {string} userId - Current user ID
 * @param {Function} onError - Error callback function
 */
export async function handleProviderClick(provider, userId, onError) {
  if (!provider) {
    onError?.('Provider not found');
    return;
  }

  const isBookr = isBookrProvider(provider);

  if (isBookr) {
    const result = await launchBookrSso(userId, {
      ttlSeconds: 300,
      returnUrl: window.location.origin,
    });
    if (!result.success) {
      onError?.(result.error);
    }
  } else if (provider.website) {
    window.open(provider.website, '_blank', 'noopener,noreferrer');
  } else {
    onError?.('No website available for this provider');
  }
}

/**
 * Get user-friendly error messages for common BOOKR SSO errors
 * @param {string} error - Error message
 * @returns {string} User-friendly error message
 */
export function getBookrErrorMessage(error) {
  const errorLower = (error || '').toLowerCase();

  if (errorLower.includes('unauthorized') || errorLower.includes('api key')) {
    return 'Authentication failed. Please contact support.';
  }

  if (errorLower.includes('user_not_found')) {
    return 'User account not found. Please contact support.';
  }

  if (errorLower.includes('popup')) {
    return 'Please allow popups for this site and try again.';
  }

  if (errorLower.includes('network') || errorLower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  return error || 'An unexpected error occurred. Please try again.';
}
